/**
 * hubRailData — the HubRail taxonomy (CD-12 / 4A).
 *
 * The Claude Design prototype (`nav-rail/data.jsx`) shape — ONE `RAIL_ITEMS`
 * registry keyed by stable id, nine DRILL sections (a lead row → grouped
 * flyout), a footer, and deep-surface pins — RECONCILED against the real app:
 * every `path` is a live deep-link and every `visible` gate reads the real
 * tab/action permission vocabulary (same helpers as config/navTree.ts), so the
 * owner tunes access in the permissions UI, not here.
 *
 * Reconciliation deltas vs. the design placeholders (reported to Michael):
 *  - Documentation moved from Operations → Tools (design put it under
 *    Tools › Comms & Docs; the old rail had it in Operations).
 *  - `scraper` and `ailedger` are LIVE surfaces here (design marked them
 *    "soon"); kept navigable — flag if they should read coming-soon.
 *  - `invoicing` has no route yet → kept `soon` with a placeholder path.
 *  - Numeric badges the design showed on To-Dos/Tickets/Comms are dropped:
 *    only inbox / helpdesk / forms / whatsnew have real count sources today.
 */
import type { RailIconKey } from './railIcons';
import type { NavGate, NavGateContext } from '../../config/navTree';

export type { NavGateContext, NavGate } from '../../config/navTree';

/** Live count badges the shell can source (others have no real feed yet). */
export type RailBadge = 'inbox' | 'helpdesk' | 'forms' | 'whatsnew';

export interface RailItem {
  id: string;
  label: string;
  iconKey: RailIconKey;
  /** Real, refresh-surviving deep link. Coming-soon items still carry the eventual path. */
  path: string;
  visible: NavGate;
  badge?: RailBadge;
  /** Honest "soon" — visible + gated, not yet navigable. */
  soon?: boolean;
}

export interface RailGroup {
  head: string;
  soon?: boolean;
  items: string[];
}

export interface RailSection {
  id: string;
  label: string;
  iconKey: RailIconKey;
  visible: NavGate;
  blurb: string;
  groups: RailGroup[];
}

/** A footer row either navigates (has `path`) OR opens a dialog (has `action`).
 *  Action rows carry no route — What's New and Submit a Request are modals, not
 *  pages, so routing them was the FR7 bug (a dead `/whats-new` fell through to
 *  the dashboard). The AppShell supplies the matching open-handler. */
export type RailFooterAction = 'whatsNew' | 'featureRequest' | 'devMode';
export interface RailFooterItem {
  id: string;
  label: string;
  iconKey: RailIconKey;
  path?: string;
  action?: RailFooterAction;
  badge?: RailBadge;
  /** Hide-don't-lock (D-04): a row whose gate fails is ABSENT, never disabled.
   *  Omit for rows everyone sees. */
  visible?: NavGate;
}

// ── gate helpers (mirror config/navTree.ts so access stays in one vocabulary) ──
const ALWAYS: NavGate = () => true;
const tab = (t: string): NavGate => (c) => c.hasTab(t);
const anyTab = (...ts: string[]): NavGate => (c) => ts.some((t) => c.hasTab(t));
const action = (a: string): NavGate => (c) => c.hasAction(a);
/** Leadership-only: the EOS leadership action OR an admin role. */
const LEADERSHIP: NavGate = (c) => c.hasAction('view_eos_leadership_l10') || c.isAdmin();

/** The single source of truth for every navigable item — real routes + gates. */
export const RAIL_ITEMS: Record<string, RailItem> = {
  // GENERAL
  'center-console': { id: 'center-console', label: 'Center Console', iconKey: 'console', path: '/dashboard', visible: ALWAYS },
  email: { id: 'email', label: 'Email', iconKey: 'mail', path: '/email', visible: anyTab('personal_inbox', 'inbox'), badge: 'inbox' },
  calendar: { id: 'calendar', label: 'Calendar', iconKey: 'calendar', path: '/scheduling/calendar', visible: tab('calendar') },
  todos: { id: 'todos', label: 'To-Dos', iconKey: 'todos', path: '/todos', visible: ALWAYS },
  herd: { id: 'herd', label: 'Mental Herd', iconKey: 'herd', path: '/knowledge-base', visible: ALWAYS },
  tracker: { id: 'tracker', label: 'Time Tracker', iconKey: 'tracker', path: '/time-tracking', visible: tab('time_tracking') },
  contacts: { id: 'contacts', label: 'Contacts', iconKey: 'contacts', path: '/contacts', visible: anyTab('sales', 'marketing', 'operations') },
  templates: { id: 'templates', label: 'Templates', iconKey: 'templates', path: '/templates', visible: ALWAYS },

  // OPERATIONS
  helpdesk: { id: 'helpdesk', label: 'Help Desk', iconKey: 'helpdesk', path: '/helpdesk', visible: anyTab('helpdesk', 'inbox'), badge: 'helpdesk' },
  tickets: { id: 'tickets', label: 'Support Tickets', iconKey: 'ticket', path: '/operations/tickets', visible: ALWAYS },
  projects: { id: 'projects', label: 'Projects', iconKey: 'projects', path: '/operations/projects', visible: ALWAYS },
  clients: { id: 'clients', label: 'Clients', iconKey: 'clients', path: '/clients', visible: ALWAYS },
  clientPortals: { id: 'clientPortals', label: 'Client Portals', iconKey: 'portal', path: '/clients/portals', visible: action('manage_client_portal_access') },

  // SALES
  // BSG Deals c4 (2026-09-18): the board renders at bare /sales and the
  // pipelines tiles index is gone, so the row is the board itself. The id
  // changed with the label (pins saved under 'pipelines' are aliased below).
  // The rail draws no per-item sub-label (only deep-surface pins carry `sub`),
  // so the design's "SALES PIPELINE" kicker has no slot here.
  deals: { id: 'deals', label: 'BSG Deals', iconKey: 'pipeline', path: '/sales', visible: tab('sales') },
  contracts: { id: 'contracts', label: 'Contracts', iconKey: 'contracts', path: '/contracts', visible: tab('sales') },
  // EVENT-1 c2 (2026-10-19): conferences as records — the list at /sales/events
  // and each event's booth board at /sales/events/:eventId. tab:sales, like the
  // router it reads.
  salesEvents: { id: 'salesEvents', label: 'Events', iconKey: 'calendar', path: '/sales/events', visible: tab('sales') },

  // MARKETING
  campaigns: { id: 'campaigns', label: 'Campaigns', iconKey: 'campaigns', path: '/sales/campaigns', visible: anyTab('sales', 'marketing') },
  newsletters: { id: 'newsletters', label: 'Newsletters', iconKey: 'newsletters', path: '/marketing/newsletters', visible: tab('marketing') },
  content: { id: 'content', label: 'Content Suite', iconKey: 'content', path: '/marketing/content', visible: tab('marketing') },
  affiliates: { id: 'affiliates', label: 'Affiliates', iconKey: 'affiliates', path: '/sales/affiliates', visible: action('view_affiliates') },

  // TOOLS
  docs: { id: 'docs', label: 'Documentation', iconKey: 'docs', path: '/documentation', visible: ALWAYS },
  comms: { id: 'comms', label: 'Comms', iconKey: 'comms', path: '/comms', visible: tab('comms') },
  forms: { id: 'forms', label: 'Forms', iconKey: 'forms', path: '/forms', visible: ALWAYS, badge: 'forms' },
  notes: { id: 'notes', label: 'Lord of the Notes', iconKey: 'notes', path: '/call-intelligence', visible: tab('call_intelligence') },
  // BK2 (Bookings 2.0, Q4 · A): its own tab key — seeded to every role that
  // could open it before (Entry 534), so the rail is unchanged on deploy.
  schedulertool: { id: 'schedulertool', label: 'Bookings', iconKey: 'scheduler', path: '/scheduling', visible: tab('scheduling') },

  // AI
  mission: { id: 'mission', label: 'Mission Control', iconKey: 'mission', path: '/mission-control', visible: action('view_ai_agents') },
  ailedger: { id: 'ailedger', label: 'Total AI Ledger', iconKey: 'ledger', path: '/mission-control/ledger', visible: action('view_ai_agents') },

  // EOS
  // DS2EOS Home — the department's front page had no row of its own.
  'eos-home': { id: 'eos-home', label: 'EOS home', iconKey: 'secEOS', path: '/eos', visible: ALWAYS },
  'eos-scorecards': { id: 'eos-scorecards', label: 'Scorecards', iconKey: 'eosScore', path: '/analytics/scorecard', visible: ALWAYS },
  'eos-rocks': { id: 'eos-rocks', label: 'Rocks', iconKey: 'rocks', path: '/eos/rocks', visible: ALWAYS },
  'eos-vto': { id: 'eos-vto', label: 'V/TO', iconKey: 'vto', path: '/eos/vto', visible: ALWAYS },
  'eos-ids': { id: 'eos-ids', label: 'IDS', iconKey: 'ids', path: '/eos/ids', visible: ALWAYS },
  'eos-accountability': { id: 'eos-accountability', label: 'Accountability Chart', iconKey: 'accountability', path: '/eos/accountability', visible: ALWAYS },
  // Track B — the People review IS what GWC was a placeholder for: gets it,
  // wants it, capacity, plus the core values and the two verdicts, one row per
  // seat. So the `eos-gwc` soon row is replaced rather than joined, and this
  // sits beside the chart it reviews. Leadership-only, hide-don't-lock.
  'eos-people': { id: 'eos-people', label: 'People review', iconKey: 'gwc', path: '/eos/people', visible: LEADERSHIP },
  // DS2EOS Home: each L10 row opens its own team. `railItemForPath` matches the path, then the query.
  'eos-l10-ops': { id: 'eos-l10-ops', label: 'L10s — Ops', iconKey: 'l10ops', path: '/eos/meetings?team=ops', visible: ALWAYS },
  'eos-l10-sm': { id: 'eos-l10-sm', label: 'L10s — Sales & Marketing', iconKey: 'l10sm', path: '/eos/meetings?team=sales-marketing', visible: ALWAYS },
  'eos-kickoff': { id: 'eos-kickoff', label: 'Kickoff', iconKey: 'kickoff', path: '/eos/kickoff', visible: ALWAYS },
  'eos-leadership': { id: 'eos-leadership', label: 'Leadership L10s', iconKey: 'leadership', path: '/eos/meetings?team=leadership', visible: LEADERSHIP },
  // EOSQ c5 — the quarterly / annual session host is LIVE, so the row is
  // clickable (no `soon`). Leadership-only, as before.
  'eos-quarterly': { id: 'eos-quarterly', label: 'Onsites', iconKey: 'eosQuarterly', path: '/eos/quarterly', visible: LEADERSHIP },
  // DS2EOS Home — live, and the visibility the wrong way round is fixed: everyone
  // has a 1:1 review (each person sees their own; the server scopes the list), and
  // the assessment is leadership's. Reading it needs the leadership key or a place
  // in a round, so anyone who can run a round keeps the door too.
  'eos-1on1': { id: 'eos-1on1', label: '1:1 reviews', iconKey: 'eosReviews', path: '/eos/one-on-ones', visible: ALWAYS },
  'eos-assessments': { id: 'eos-assessments', label: 'EOS assessment', iconKey: 'eosAssess', path: '/eos/assessments', visible: (c) => LEADERSHIP(c) || c.hasAction('manage_eos_assessments') },

  // ANALYTICS
  scorecards: { id: 'scorecards', label: 'Scorecards', iconKey: 'scorecard', path: '/analytics/scorecard', visible: tab('scorecards') },
  forecasting: { id: 'forecasting', label: 'Forecasting', iconKey: 'forecast', path: '/analytics/forecasting', visible: anyTab('forecasting', 'capacity') },
  timereports: { id: 'timereports', label: 'Time Reports', iconKey: 'reports', path: '/analytics/time-reports', visible: anyTab('time_reports', 'time_tracking') },
  // Data Center (S4) — reserved in the Analytics door; honest soon until it lands.
  datacenter: { id: 'datacenter', label: 'Data Center', iconKey: 'scorecard', path: '/analytics/data-center', visible: anyTab('scorecards', 'eos') },
  finances: { id: 'finances', label: 'Finances', iconKey: 'finance', path: '/analytics/finances', visible: tab('finances') },
  invoicing: { id: 'invoicing', label: 'Invoicing', iconKey: 'invoice', path: '/analytics/finances/invoicing', visible: tab('finances'), soon: true },

  // ADMIN
  engineering: { id: 'engineering', label: 'Engineering', iconKey: 'engineering', path: '/engineering', visible: tab('engineering') },
  smoketests: { id: 'smoketests', label: 'Smoke Tests', iconKey: 'smoketests', path: '/smoke-tests', visible: action('manage_settings') },
};

/** Nine DRILL sections — each a lead row that opens a grouped flyout. */
export const RAIL_SECTIONS: RailSection[] = [
  {
    id: 'general', label: 'General', iconKey: 'secGeneral', visible: ALWAYS,
    blurb: 'Your daily workspace — inbox, calendar, tasks and capture',
    groups: [
      { head: 'Workspace', items: ['center-console', 'email', 'calendar', 'todos'] },
      { head: 'Capture', items: ['herd', 'tracker', 'contacts', 'templates'] },
    ],
  },
  {
    id: 'ops', label: 'Operations', iconKey: 'secOps', visible: tab('operations'),
    blurb: 'Delivery — help desk, projects, tickets and clients',
    groups: [
      { head: 'Service', items: ['helpdesk', 'tickets'] },
      { head: 'Delivery', items: ['projects', 'clients', 'clientPortals'] },
    ],
  },
  {
    id: 'sales', label: 'Sales', iconKey: 'secSales', visible: tab('sales'),
    blurb: 'Revenue — the pipeline, events, contracts and prospecting',
    groups: [
      { head: 'Pipeline', items: ['deals', 'salesEvents', 'contracts'] },
    ],
  },
  {
    id: 'marketing', label: 'Marketing', iconKey: 'secMarketing', visible: tab('marketing'),
    blurb: 'Reach — campaigns, sends, content and partners',
    groups: [
      { head: 'Outbound', items: ['campaigns', 'newsletters'] },
      { head: 'Content & Partners', items: ['content', 'affiliates'] },
    ],
  },
  {
    id: 'tools', label: 'Tools', iconKey: 'secTools', visible: ALWAYS,
    blurb: 'Utilities — docs, comms, forms and scheduling',
    groups: [
      { head: 'Comms & Docs', items: ['docs', 'comms', 'forms'] },
      { head: 'Utilities', items: ['notes', 'schedulertool'] },
    ],
  },
  {
    id: 'ai', label: 'AI', iconKey: 'secAI', visible: action('view_ai_agents'),
    blurb: 'Intelligence — Mission Control: the office, the agents and their spend',
    groups: [
      // Mission Control 2.0 (Entry 536): the Total AI Ledger is Spend, a tab of Mission Control
      // (/mission-control/ledger redirects there), so its own door left the rail (brief §4).
      { head: 'Control', items: ['mission'] },
    ],
  },
  {
    id: 'eos', label: 'EOS', iconKey: 'secEOS', visible: tab('eos'),
    blurb: 'Traction — the operating system that runs the company',
    groups: [
      { head: 'Traction Tools', items: ['eos-home', 'eos-scorecards', 'eos-rocks', 'eos-vto', 'eos-ids', 'eos-accountability'] },
      { head: 'Meetings', items: ['eos-l10-ops', 'eos-l10-sm', 'eos-kickoff', 'eos-leadership', 'eos-quarterly'] },
      // DS2EOS Home — the home's third group, by the same name.
      { head: 'People', items: ['eos-people', 'eos-assessments', 'eos-1on1'] },
    ],
  },
  {
    id: 'analytics', label: 'Analytics', iconKey: 'secAnalytics',
    visible: anyTab('scorecards', 'insights', 'forecasting', 'capacity', 'finances', 'time_reports'),
    blurb: 'Measurement — scorecards, forecasts and the numbers',
    groups: [
      { head: 'Performance', items: ['scorecards', 'forecasting', 'timereports', 'datacenter'] },
      { head: 'Finance', items: ['finances', 'invoicing'] },
    ],
  },
  {
    id: 'admin', label: 'Admin', iconKey: 'secAdmin',
    visible: (c) => c.isAdmin() || c.hasTab('engineering'),
    blurb: 'Workshop — internal build, engineering & system tools',
    groups: [
      { head: 'Workshop', items: ['engineering'] },
      { head: 'System', items: ['smoketests'] },
    ],
  },
];

/** Footer rows (bottom, always present). */
export const RAIL_FOOTER: RailFooterItem[] = [
  { id: 'whatsnew', label: "What's New", iconKey: 'whatsnew', action: 'whatsNew', badge: 'whatsnew' },
  { id: 'feature-request', label: 'Submit a Request', iconKey: 'feedback', action: 'featureRequest' },
  // Dev Mode — the debug-capture switch that powers smoke-test evidence and bug
  // reports. It used to live in the legacy MUI rail's admin block; when CD-12
  // mounted HubRail for every company user (AppShell's `isPersonalView ?
  // <Paper> : <HubRail>` split), that block stopped rendering for ANYONE — the
  // only users who still reach it are personal-view team members, for whom its
  // own `isAdmin()` check is false by construction. So the toggle was not
  // removed, it was orphaned, and dev mode has been reachable only via
  // Ctrl+Shift+D since 2026-07-18. Restored here rather than in AppShell, which
  // is frozen.
  { id: 'devmode', label: 'Dev Mode', iconKey: 'bug', action: 'devMode', visible: (c) => c.isAdmin() },
  { id: 'settings', label: 'Settings', iconKey: 'settings', path: '/settings' },
];

export const DEFAULT_SECTION_ORDER: string[] = RAIL_SECTIONS.map((s) => s.id);

const SECTION_BY_ID: Record<string, RailSection> = {};
RAIL_SECTIONS.forEach((s) => { SECTION_BY_ID[s.id] = s; });
export const sectionById = (id: string): RailSection | undefined => SECTION_BY_ID[id];

/** item id → origin section id (so a favorite knows which flyout to leave a reflection in). */
export const ITEM_ORIGIN: Record<string, string> = {};
RAIL_SECTIONS.forEach((s) => {
  s.groups.forEach((g) => g.items.forEach((id) => { ITEM_ORIGIN[id] = s.id; }));
});

export const drillItemIds = (s: RailSection): string[] =>
  s.groups.reduce<string[]>((a, g) => a.concat(g.items), []);

/** "N tools" count on a drill lead — visible, non-soon items only. */
export const drillCount = (s: RailSection, ctx: NavGateContext): number =>
  drillItemIds(s).filter((id) => {
    const it = RAIL_ITEMS[id];
    return it && !it.soon && it.visible(ctx);
  }).length;

export const allRailItems = (): RailItem[] => Object.values(RAIL_ITEMS);

/**
 * Existing 'nav' rail-pins were saved with the OLD config/navTree.ts item KEYS
 * (e.g. 'Dashboard', 'PersonalInbox'); HubRail keys items by new ids
 * ('center-console', 'email'). Map old→new so a user's server-backed favorites
 * survive the id change. Unknown ids pass through (a new pin already uses the
 * new id).
 */
const NAV_PIN_ALIAS: Record<string, string> = {
  Dashboard: 'center-console', PersonalInbox: 'email', 'Time Tracking': 'tracker', Todos: 'todos',
  KnowledgeBase: 'herd', Comms: 'comms', Forms: 'forms', 'Call Intelligence': 'notes',
  Scheduling: 'schedulertool', Calendar: 'calendar', AiMissionControl: 'mission', AiLedger: 'ailedger',
  EosScorecards: 'eos-scorecards', EosRocks: 'eos-rocks', EosVto: 'eos-vto', EosIds: 'eos-ids',
  EosAccountability: 'eos-accountability', EosL10Ops: 'eos-l10-ops', EosL10Sales: 'eos-l10-sm',
  EosKickoff: 'eos-kickoff', EosLeadershipL10: 'eos-leadership', HelpDesk: 'helpdesk',
  OpsProjects: 'projects', OpsTickets: 'tickets', Clients: 'clients', 'Documentation Nation': 'docs',
  SalesPipelines: 'deals', SalesEvents: 'salesEvents', Campaigns: 'campaigns', Affiliates: 'affiliates', ContentSuite: 'content',
  Newsletters: 'newsletters', Proposals: 'contracts', SalesScraper: 'scraper', Contacts: 'contacts',
  Templates: 'templates', Scorecards: 'scorecards', Capacity: 'forecasting', 'Time Reports': 'timereports',
  Finances: 'finances', Engineering: 'engineering',
  SmokeTests: 'smoketests',
  // BSG Deals c4: pins saved under the CD-12 id keep resolving to the board.
  pipelines: 'deals',
};

/** Resolve a 'nav' pin's surface_id to a current RAIL_ITEMS id (alias-aware). */
export const resolveNavPinId = (surfaceId: string): string =>
  RAIL_ITEMS[surfaceId] ? surfaceId : (NAV_PIN_ALIAS[surfaceId] ?? surfaceId);

/** Active-highlight: longest-path-prefix match against a location, skipping soon rows. */
export function railItemForPath(pathname: string, search = ''): RailItem | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  let best: RailItem | null = null;
  let bestLen = -1;
  let bestQueryHit = false;
  // DS2EOS Home: a row's query (`?team=ops`) chooses between rows sharing a path; it
  // never stops one matching. A meeting at /eos/meetings/:id names no team, so the
  // first of them lights, as it did when all three shared one path.
  const params = new URLSearchParams(search);
  for (const it of Object.values(RAIL_ITEMS)) {
    if (it.soon) continue;
    const [path, query] = it.path.split('?');
    if (clean === path || clean.startsWith(path + '/')) {
      const queryHit = Boolean(query) && Array.from(new URLSearchParams(query).entries()).every(([k, v]) => params.get(k) === v);
      if (path.length > bestLen || (path.length === bestLen && queryHit && !bestQueryHit)) {
        best = it; bestLen = path.length; bestQueryHit = queryHit;
      }
    }
  }
  return best;
}
